import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * A simplified version of the PageContentCommandResult for embedded pages.
 */
export type EmbeddedPageContentCommandResult = {
	contentType: 'embeddedPage';
	embeddedPageBody: string;
	contentId: string;
};

export type PageContentCommandResult = {
	contentType: 'page';
	/**
	 * The ADF of the Confluence page.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	pageBody: any;
	/**
	 * The contentId of the Confluence page being interacted with
	 */
	contentId: string;
	/**
	 * The selected ADF in the Confluence page.
	 */
	selectionFragment: string;
	/**
	 * The selected ADF local ids in the Confluence page.
	 */
	selectionLocalIds: string;
	/**
	 * The current selection in markdown
	 */
	selectionMarkdown: string;
	/**
	 * The start index of the selection within the first node of the selection
	 */
	startIndex?: number;
	/**
	 * The start index of the selection within the last node of the selection
	 */
	endIndex?: number;
	/**
	 * The Maui content ID extracted from the ADF.
	 */
	contentMauiId?: string;

	/**
	 * Additional context about the MAUI app that is being interacted with.
	 */
	dynamicUiSource?: {
		contentType: string;
		startLocalId?: string;
		endLocalId?: string;
		startIndex?: number;
		endIndex?: number;
		fragmentAdf?: string;
	};
	/**
	 * The dynamic UI type of the page.
	 */
	dynamicUiType?: string;
	/**
	 * Whether the page is in view mode (not editing)
	 */
	isViewMode?: boolean;
	/**
	 * Whether the draft is locked for editing due to an active approval workflow.
	 * When true, isViewMode will also be true, but this flag lets the backend
	 * distinguish the reason so it can surface an appropriate message.
	 */
	isDraftLockedForEditing?: boolean;
	/**
	 * Use the generic editor minion - this enables access to the editor minion
	 * without a product specific minion
	 */
	useGenericEditorSkill?: boolean;
	/**
	 * When the selection is a single media node (e.g. image), the file id for that media.
	 * Used for creationContext when the user asks Rovo about the selected media.
	 */
	mediaFileId?: string;
	/**
	 * When the selection is a single media node (e.g. image), the collection id for that media.
	 * Sent to the backend via `creationContext.additionalContext.mediaCollection` so it can resolve
	 * the current media's collection when editing an existing image.
	 */
	mediaCollection?: string;
	/**
	 * Additional context for products.
	 */
	additionalContext?: Record<string, SerializableValue>;
	/**
	 * Frontend → backend capability negotiation for the HTML+ incremental
	 * tool-call streaming contract.
	 *
	 * Per the BE-aligned FE spec §2.5 + §3.8, a single int that backend reads
	 * to opt the request into the v2 wire format. Must be a JSON **number**
	 * (not a stringified number); backend rejects strings / booleans /
	 * fractions / null / negatives and falls back to `1`.
	 *
	 * - `2` → frontend supports v2 provisional-snapshot streaming. Backend
	 *   may emit v2 envelopes (with `id`/`sequence`/`status`) for streamable
	 *   tool calls; non-streamable tool calls continue to flow through the
	 *   legacy v1 path regardless.
	 * - Omitted (the only other value the FE ever sends) → backend defaults
	 *   to v1. The field is omitted entirely when the
	 *   `platform_editor_use_html_plus_streaming_parser` exp-val is off, so
	 *   the v1 wire bytes stay identical to today.
	 */
	editorStreamingContractVersion?: number;
	/**
	 * Whether to use the fast path for the editor minion.
	 * When true, the backend will skip the orchestrator and call the editor minion directly.
	 */
	useFastPath?: boolean;
};

export type WhiteboardContentCommandResult = {
	contentType: 'whiteboard';
	/**
	 * SVG representation of the whiteboard.
	 */
	svg: string;
	/**
	 * The contentId of the Confluence whiteboard being interacted with
	 */
	contentId: string;
	/**
	 * The WDF representation of the whiteboard.
	 */
	wdf: string;

	/**
	 * The title of the whiteboard.
	 */
	title: string;
	/**
	 * The URL of the whiteboard.
	 */
	url: string;
	/**
	 * The currently selected element ids.
	 */
	selectedElementIds: string[];
	/**
	 * Whether the whiteboard is in view/read only mode (not editing)
	 */
	isViewMode?: boolean;
};

type DatabaseContentCommandResult = {
	contentType: 'database';
	/**
	 * The contentId of the Confluence database being interacted with
	 */
	contentId: string;
	csv: string;
	title: string;
	url: string;
	/**
	 * Whether the database is in view mode (not editing)
	 */
	isViewMode?: boolean;
};

export type SlidesContentCommandResult = {
	contentType: 'slide';
	/**
	 * The XML representation of the slides.
	 */
	xml: string;
	/**
	 * The title of the slides.
	 */
	title: string;
	/**
	 * The URL of the slides.
	 */
	url: string;
	/**
	 * The contentId of the slides being interacted with.
	 */
	contentId: string;
	/**
	 * The currently selected slide index.
	 */
	selectedSlideIndex: number;
	/**
	 * The currently selected element ids.
	 */
	selectedElementIds: string[];
	/**
	 * Whether the slides are in view mode (not editing)
	 */
	isViewMode?: boolean;
};

export type DashboardContentCommandResult = {
	contentType: 'dashboard';
	/**
	 * The ARI of the AVP dashboard being interacted with.
	 */
	dashboardAri: string;
	/**
	 * The JSON-stringified result of avp_getDashboard.
	 */
	content: string;
	/**
	 * Whether the dashboard is currently being edited.
	 */
	isEditing: boolean;
	/**
	 * The AI context string for the dashboard charts and filters.
	 */
	chartResponseData?: string;
	/**
	 * The dashboard experience e.g. jsm-summary, jsw-summary
	 */
	location: string | undefined;
	/**
	 * Available products for metrics
	 */
	products: string[];
	/**
	 * Available subproducts e.g. jsm
	 */
	subProducts: string[];
};

/**
 * Properties for the result of the read content command.
 */
export type ReadContentCommandResult =
	| PageContentCommandResult
	| EmbeddedPageContentCommandResult
	| WhiteboardContentCommandResult
	| DatabaseContentCommandResult
	| SlidesContentCommandResult
	| DashboardContentCommandResult;

export type ReadContentCommandProperties = CommandProperties & {
	/**
	 * The Rovo interaction source that requested content, used by receivers to distinguish entry points
	 * such as Confluence inline editor from other chat or creation flows.
	 */
	source?: string;
	/**
	 * The prompt ADF submitted by the user. Receivers can inspect this before advertising
	 * request capabilities that depend on prompt shape.
	 */
	prompt?: SerializableValue;
};

/**
 * A command sent by a bridge API client to return the current content of a content type. It will have a different result type for each content type.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to return the current content of a content type.
 */
export const ReadContentCommand: CommandConstructor<
	ReadContentCommandProperties,
	ReadContentCommandResult
> = createCommand<ReadContentCommandProperties, ReadContentCommandResult>('read-content-command');
